import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader } from "@lawline/ui/components/card";
import { Skeleton } from "@lawline/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";

import { useAuth } from "@platform/auth/context";
import { formatDate } from "@platform/fmt";
import { revokeKeyMutation } from "@platform/features/api-keys/api-keys.mutations";
import { apiKeysQueryOptions } from "@platform/features/api-keys/api-keys.queries";
import type { ApiKey } from "@platform/features/api-keys/api-keys.types";
import {
	CONNECTION_PREFIX,
	effectiveStatus,
	isConnection,
} from "@platform/features/api-keys/api-keys.utils";

// A lapsed or revoked connection no longer reaches the organisation, so only live ones count.
function connectedApps(keys: readonly ApiKey[]): ApiKey[] {
	return keys.filter((key) => isConnection(key) && effectiveStatus(key) === "active");
}

function appName(key: ApiKey): string {
	const name = key.name ?? "";
	return name.startsWith(CONNECTION_PREFIX) ? name.slice(CONNECTION_PREFIX.length) : name;
}

// The connections are the organisation's MCP keys, so they share the API keys list and its revoke.
// A revoke refetches that list, which drops the app from here and from the API keys page.
export function ConnectedApps() {
	const t = useTranslations("aiApps.connected");
	const { can } = useAuth();
	const keys = useQuery(apiKeysQueryOptions);
	const revoke = useMutation(revokeKeyMutation);
	// The target outlives the open flag, so the dialog keeps its title while it closes.
	const [target, setTarget] = useState<ApiKey | null>(null);
	const [confirming, setConfirming] = useState(false);

	const canRevoke = can({ apiKey: ["revoke"] });
	const apps = keys.data ? connectedApps(keys.data) : [];
	// Each approving user holds a connection of their own, so the app name alone can repeat.
	const person = (app: ApiKey) => app.creator?.name || app.creator?.email || t("unknownCreator");

	return (
		<Card>
			<CardHeader>
				<h2 className="text-base font-medium">{t("title")}</h2>
				<CardDescription>{t("description")}</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-3">
				{revoke.isError ? (
					<p role="alert" className="text-destructive">
						{t("revokeFailed")}
					</p>
				) : null}
				{keys.isPending ? (
					<Skeleton className="h-16 w-full" />
				) : keys.isError ? (
					<p className="text-destructive">{t("error")}</p>
				) : apps.length === 0 ? (
					<p className="text-muted-foreground">{t("empty")}</p>
				) : (
					<div className="overflow-hidden rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("columns.app")}</TableHead>
									<TableHead>{t("columns.creator")}</TableHead>
									<TableHead>{t("columns.created")}</TableHead>
									<TableHead>{t("columns.lastUsed")}</TableHead>
									<TableHead>{t("columns.lapses")}</TableHead>
									{canRevoke ? (
										<TableHead>
											<span className="sr-only">{t("revoke")}</span>
										</TableHead>
									) : null}
								</TableRow>
							</TableHeader>
							<TableBody>
								{apps.map((app) => (
									<TableRow key={app.id}>
										<TableCell className="font-medium">{appName(app)}</TableCell>
										<TableCell>
											{app.creator ? (
												<div className="flex flex-col">
													<span>{app.creator.name || app.creator.email}</span>
													{app.creator.name ? (
														<span className="text-muted-foreground text-xs">
															{app.creator.email}
														</span>
													) : null}
												</div>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell>{formatDate(app.createdAt)}</TableCell>
										<TableCell>{formatDate(app.lastUsedAt)}</TableCell>
										<TableCell>{formatDate(app.expiresAt)}</TableCell>
										{canRevoke ? (
											<TableCell className="text-right">
												<Button
													variant="ghost"
													size="sm"
													aria-label={t("revokeLabel", {
														app: appName(app),
														person: person(app),
														date: formatDate(app.createdAt),
													})}
													onClick={() => {
														revoke.reset();
														setTarget(app);
														setConfirming(true);
													}}
												>
													{t("revoke")}
												</Button>
											</TableCell>
										) : null}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</CardContent>

			<AlertDialog open={confirming} onOpenChange={setConfirming}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{target ? t("revokeTitle", { app: appName(target), person: person(target) }) : null}
						</AlertDialogTitle>
						<AlertDialogDescription>{t("revokeDescription")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("revokeCancel")}</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => target && revoke.mutate(target.id)}
						>
							{t("revokeConfirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Card>
	);
}
